import { useEffect, useState } from "react";
import NavBar from "../../component/navBar/NavBar";
import { useAuth } from "../../config/utilities/useAuth";
import {
  APICreateVehicle,
  APIDeleteVehicles,
  APIGetVehicles,
  APIUpdateVehicle,
} from "../../config/utilities/APIs/vehiclesApi";
import Title from "../../component/title/Title";
import Table from "../../component/table/Table";
import { useSnackbar } from "../../component/Snackbar/SnackbarProvider";
import DeleteModal from "../../component/modal/DeleteModal";
import { VehicleForm } from "./VehicleForm";
import { formatCapitalize } from "../../config/utilities/formatCapitalize";
import DeactivationModal from "../../component/modal/DeactivationModal";
import "./Vehicles.css";

const headers = [
  { key: "busNumber", name: "Bus Number", sortable: false },
  { key: "vehicleNumber", name: "Registration Number", sortable: false },
  { key: "brand", name: "Brand", sortable: false },
  { key: "model", name: "Model", sortable: false },
  { key: "color", name: "Color", sortable: false },
  { key: "capacity", name: "Capacity", sortable: false },
  { key: "status", name: "Status", sortable: false },
];

const statusOptions = [
  { label: "Active", value: "active" },
  { label: "Inactive", value: "inactive" },
];

export default function Vehicles() {
  const { privilege, token } = useAuth();
  const { showSuccess, showError, show } = useSnackbar();
  const role = privilege?.role;
  const institutionId = privilege?.institutionId;
  const [action, setAction] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [fetchedVehicleData, setFetchedVehicleData] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [selectedFilters, setSelectedFilters] = useState({
    status: "",
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [showDeactivationModal, setShowDeactivationModal] = useState(false);

  const filters = [
    { name: "status", placeholder: "Filter by Status", options: statusOptions },
  ];

  const [input, setInput] = useState({
    busNumber: "",
    vehicleNumber: "",
    brand: "",
    model: "",
    color: "",
    capacity: "",
    status: "active",
    isTelematic: false,
    useTelematic: false,
  });

  const [error, setError] = useState({
    busNumber: false,
    vehicleNumber: false,
    brand: false,
    model: false,
    color: false,
    capacity: false,
  });

  const fetchVehicles = async (
    page = 1,
    filters = selectedFilters,
    searchTerm
  ) => {
    setLoading(true);

    try {
      const filterParams = Object.fromEntries(
        Object.entries(filters).filter(([_, v]) => v !== "")
      );
      const response = await APIGetVehicles(
        page,
        filterParams,
        setLoading,
        institutionId,
        searchTerm
      );

      if (response.status === 200) {
        const { data } = response.data;
        setFetchedVehicleData(data);
        setPageNumber(page);
      } else if (response.status === 400) {
        console.warn("Bad Request:", response);
      } else if (response.status === 401) {
        console.warn("Unauthorized:", response);
      } else if (response.status === 403) {
        console.warn("Forbidden:", response);
      } else if (response.status === 404) {
        console.warn("Not Found:", response);
      } else if (response.status >= 500) {
        console.error("Server Error:", response);
      } else {
        console.warn("Unexpected Status Code:", response.status, response);
      }
    } catch (error) {
      console.error("Error fetching vehicles:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVehicles();
  }, []);

  useEffect(() => {
    fetchVehicles(1, selectedFilters, searchTerm);
  }, [selectedFilters, searchTerm]);

  const handleReset = async () => {
    setInput({
      busNumber: "",
      vehicleNumber: "",
      brand: "",
      model: "",
      color: "",
      capacity: "",
      status: "active",
      isTelematic: false,
      useTelematic: false,
    });
    setError({
      busNumber: false,
      vehicleNumber: false,
      brand: false,
      model: false,
      color: false,
      capacity: false,
    });
    setFetchedVehicleData([]);
    setShowDeactivationModal(false);
    setShowDeleteModal(false);
    setSelectedRowData(null);
    setAction(null);
    setPageNumber(1);
    await fetchVehicles(1, selectedFilters, searchTerm);
  };

  const handleDelete = async (row) => {
    setLoading(true);

    const rowId = row._id;

    try {
      const response = await APIDeleteVehicles(
        row.vehicleNumber,
        setLoading,
        institutionId,
        token
      );

      if (response?.status === 200) {
        showSuccess("Delete Successful", "Vehicle has been retired successfuly");
      } else {
        throw new Error(
          response?.response?.data?.message || "Failed to retire vehicle"
        );
      }
    } catch (error) {
      showError("Retire Failed", error.message || "An error occurred");
    } finally {
      handleReset();
    }
  };

  const handleEdit = async () => {
    setLoading(true);

    const formattedInput = formatInput(input);

    try {
      const response = await APIUpdateVehicle(
        formattedInput,
        setLoading,
        institutionId,
        token
      );

      if (response?.status === 200) {
        showSuccess("Update Successful", "Vehicle has been updated successfuly");
      } else {
        throw new Error(
          response?.response?.data?.message || "Failed to update vehicle"
        );
      }
    } catch (error) {
      showError("Update Failed", error.message || "An error occurred");
    } finally {
      handleReset();
    }
  };

  const handleCreate = async () => {
    setLoading(true);

    const formattedInput = formatInput(input);

    try {
      const response = await APICreateVehicle(
        formattedInput,
        setLoading,
        institutionId,
        token
      );

      if (response?.status === 200) {
        showSuccess("Create Successful", "Vehicle has been created successfuly");
      } else {
        throw new Error(
          response?.response?.data?.message || "Failed to create vehicle"
        );
      }
    } catch (error) {
      showError("Create Failed", error.message || "An error occurred");
    } finally {
      handleReset();
    }
  };

  const handleStatus = async (action) => {
    setLoading(true);
    let status;
    let title;
    if (action === "activate") {
      status = "active";
      title = "Activation";
    } else {
      status = "inactive";
      title = "Deactivation";
    }

    const payload = { status, vehicleNumber: selectedRowData.vehicleNumber };
    try {
      await APIUpdateVehicle(payload, setLoading, institutionId, token);
    } catch (error) {
      showError(`${title} Failed`, error.message || "An error occurred");
    } finally {
      handleReset();
    }
  };

  const handlePopoverAction = (action, rowId) => {
    const selectedRow = fetchedVehicleData.vehicles.find(
      (row) => row._id === rowId
    );
    if (action === "edit") {
      setSelectedRowData(selectedRow);
      setAction("edit");
    } else if (action === "delete") {
      setSelectedRowData(selectedRow);
      setShowDeleteModal(true);
    } else if (action === "activate") {
      setSelectedRowData(selectedRow);
      setAction("activate");
      setShowDeactivationModal(true);
    } else if (action === "deactivate") {
      setSelectedRowData(selectedRow);
      setAction("deactivate");
      setShowDeactivationModal(true);
    } else {
      setSelectedRowData(selectedRow);
      setAction("view");
    }
  };

  const handleOpenCreate = () => {
    setAction("create");
  };

  const handleTelematicsCheckFeedback = ({ type, title, subtitle }) => {
    show({
      type: type || "success",
      title: title || "Telematics Check",
      subtitle: subtitle || "",
    });
  };

  const formatInput = (input) => {
    const formattedInput = {
      busNumber: input.busNumber.trim(),
      vehicleNumber: input.vehicleNumber.trim().toUpperCase(),
      brand: formatCapitalize(input.brand).trim(),
      model: formatCapitalize(input.model).trim(),
      color: formatCapitalize(input.color).trim(),
      capacity: Number(input.capacity),
      status: input.status,
      useTelematic: input.useTelematic === true,
    };
    return formattedInput;
  };

  const renderTitle = () => {
    switch (action) {
      case "edit":
        return (
          <Title
            title="Edit Vehicle"
            titleSize="1.5rem"
            gap="0.5rem"
            lineHeight="2.5rem"
          />
        );
      case "create":
        return (
          <Title
            title="Create Vehicle"
            titleSize="1.5rem"
            gap="0.5rem"
            lineHeight="2.5rem"
          />
        );
      case "view":
        <Title
          title="View Vehicle"
          titleSize="1.5rem"
          gap="0.5rem"
          lineHeight="2.5rem"
        />;
      default:
        return (
          <Title
            title="Vehicle Management"
            subtitle="Manage all the vehicles in your institution"
            titleSize="1.5rem"
            subtitleSize="1rem"
            gap="0.5rem"
            lineHeight="2rem"
          />
        );
    }
  };

  const renderContent = () => {
    switch (action) {
      case "edit":
        return (
          <VehicleForm
            mode="edit"
            existingData={selectedRowData}
            onSubmit={handleEdit}
            handleReset={handleReset}
            loading={loading}
            setLoading={setLoading}
            input={input}
            setInput={setInput}
            error={error}
            setError={setError}
            role={role}
            token={token}
            institutionId={institutionId}
            onTelematicsCheckFeedback={handleTelematicsCheckFeedback}
          />
        );
      case "create":
        return (
          <VehicleForm
            mode="create"
            onSubmit={handleCreate}
            handleReset={handleReset}
            loading={loading}
            setLoading={setLoading}
            input={input}
            setInput={setInput}
            error={error}
            setError={setError}
            token={token}
            institutionId={institutionId}
            onTelematicsCheckFeedback={handleTelematicsCheckFeedback}
          />
        );
      case "view":
        return (
          <VehicleForm
            mode="view"
            existingData={selectedRowData}
            handleReset={handleReset}
            loading={loading}
            setLoading={setLoading}
            input={input}
            setInput={setInput}
            error={error}
            setError={setError}
            token={token}
            institutionId={institutionId}
            onTelematicsCheckFeedback={handleTelematicsCheckFeedback}
          />
        );
      default:
        return (
          <Table
            data={fetchedVehicleData?.vehicles || []}
            searchText="Search by bus or registration number..."
            headers={headers}
            showEmptyHeader={true}
            showFilter={true}
            handlePopoverAction={handlePopoverAction}
            onClick={handleOpenCreate}
            module="vehicles"
            loading={loading}
            showTableAction={
              role === "ADMIN" || role === "SUPERADMIN" || role === "EDITOR"
            }
            height="75vh"
            useBackendPagination={true}
            onPageChange={fetchVehicles}
            totalPages={fetchedVehicleData?.pagination?.totalPages || 1}
            currentPage={pageNumber}
            filters={filters}
            selectedFilters={selectedFilters}
            setSelectedFilters={setSelectedFilters}
            totalRecords={fetchedVehicleData?.pagination?.total || 0}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
          />
        );
    }
  };

  return (
    <div className="main-page vehicles-page">
      <NavBar type="vehicles" action={action} handleReset={handleReset} />
      <div className="content-area vehicles-content-area">
        <div className="title-container mobile-hide-page-title">{renderTitle()}</div>
        <div className="main-content-container vehicles-main-content-container">{renderContent()}</div>
      </div>
      <DeleteModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onDelete={handleDelete}
        itemName={selectedRowData}
        module="vehicles"
        loading={loading}
      />
      <DeactivationModal
        isOpen={showDeactivationModal}
        onClose={() => setShowDeactivationModal(false)}
        loading={loading}
        onToggle={handleStatus}
        itemName={selectedRowData}
        module="vehicles"
        action={action}
      />
    </div>
  );
}
